import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { Mail, ChevronRight } from 'lucide-react';
import { API, TIPOS_SEGURO } from './constants';

const EMPTY = { nombre: '', email: '', telefono: '', tipoSeguro: '' };
const inputCls = 'bg-slate-50 border-slate-200 focus:border-blue-500 focus:ring-blue-500/20 rounded-xl h-12';

export const NewsletterForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState(EMPTY);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, formData);
      toast({
        title: '¡Suscripción exitosa!',
        description: 'Revisa tu email: te enviamos la bienvenida con tu código de descuento WELCOME10.',
        duration: 8000,
      });
      setFormData(EMPTY);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast({
        title: 'Error en la suscripción',
        description: typeof detail === 'string' ? detail : 'Error al procesar la suscripción. Intenta nuevamente.',
        variant: 'destructive',
        duration: 6000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 relative" data-testid="newsletter-section">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-white/80 backdrop-blur-md shadow-2xl border-0 rounded-3xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-4">
                <Mail className="h-8 w-8 text-white" />
              </div>
              <CardTitle className="text-3xl font-bold text-white mb-4">Newsletter Exclusivo</CardTitle>
              <CardDescription className="text-white/90 text-lg">
                Sé el primero en conocer las mejores ofertas y consejos de expertos
              </CardDescription>
            </div>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="newsletter-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="nombre" className="text-slate-700 font-semibold">Nombre completo</Label>
                    <Input id="nombre" name="nombre" type="text" placeholder="Tu nombre" value={formData.nombre} onChange={handleInputChange} required className={inputCls} data-testid="newsletter-nombre-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-semibold">Email</Label>
                    <Input id="email" name="email" type="email" placeholder="tu@email.com" value={formData.email} onChange={handleInputChange} required className={inputCls} data-testid="newsletter-email-input" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="telefono" className="text-slate-700 font-semibold">Teléfono (opcional)</Label>
                  <Input id="telefono" name="telefono" type="tel" placeholder="+58 414 123 4567" value={formData.telefono} onChange={handleInputChange} className={inputCls} data-testid="newsletter-telefono-input" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tipoSeguro" className="text-slate-700 font-semibold">Tipo de seguro de interés</Label>
                  <select
                    id="tipoSeguro"
                    name="tipoSeguro"
                    value={formData.tipoSeguro}
                    onChange={handleInputChange}
                    data-testid="newsletter-tipo-select"
                    className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  >
                    <option value="">Selecciona una opción</option>
                    {TIPOS_SEGURO.map((tipo) => <option key={tipo} value={tipo}>{tipo}</option>)}
                  </select>
                </div>

                <Button
                  type="submit"
                  data-testid="newsletter-submit-btn"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-300"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Procesando...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Mail className="h-5 w-5" />
                      <span>Suscribirme gratis</span>
                      <ChevronRight className="h-5 w-5" />
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default NewsletterForm;
